import { useEffect, useState } from "react";

/**
 * Layout signals for both the battle page and the rest of the app.
 *
 * Battle-only:
 *  - `tier`: comfortable (≥1440px) vs. compact (<1440px) battle layout.
 *  - `swap`: true when window height <900px — sidebar drops one of preview/log.
 *
 * App-wide:
 *  - `narrow`: width <800px. Pages with side-by-side panels (deck builder,
 *    admin cards, etc.) stack vertically once this is true.
 *  - `cramped`: width <1100px. Header rows with many buttons collapse into a
 *    menu (lobby) or wrap (admin tabs) once this is true.
 */
export interface ResponsiveLayout {
  tier: "comfortable" | "compact";
  swap: boolean;
  narrow: boolean;
  cramped: boolean;
}

const COMFORTABLE_MIN_WIDTH = 1440;
const TALL_MIN_HEIGHT = 900;
const NARROW_MAX_WIDTH = 800;
const CRAMPED_MAX_WIDTH = 1100;

export function useResponsiveLayout(): ResponsiveLayout {
  const [layout, setLayout] = useState<ResponsiveLayout>(compute);

  useEffect(() => {
    const update = () => setLayout(compute());
    const widthQ = window.matchMedia(`(min-width: ${COMFORTABLE_MIN_WIDTH}px)`);
    const heightQ = window.matchMedia(`(min-height: ${TALL_MIN_HEIGHT}px)`);
    const narrowQ = window.matchMedia(`(max-width: ${NARROW_MAX_WIDTH - 1}px)`);
    const crampedQ = window.matchMedia(`(max-width: ${CRAMPED_MAX_WIDTH - 1}px)`);
    widthQ.addEventListener("change", update);
    heightQ.addEventListener("change", update);
    narrowQ.addEventListener("change", update);
    crampedQ.addEventListener("change", update);
    return () => {
      widthQ.removeEventListener("change", update);
      heightQ.removeEventListener("change", update);
      narrowQ.removeEventListener("change", update);
      crampedQ.removeEventListener("change", update);
    };
  }, []);

  return layout;
}

function compute(): ResponsiveLayout {
  if (typeof window === "undefined") {
    return { tier: "comfortable", swap: false, narrow: false, cramped: false };
  }
  const w = window.innerWidth;
  const tall = window.innerHeight >= TALL_MIN_HEIGHT;
  return {
    tier: w >= COMFORTABLE_MIN_WIDTH ? "comfortable" : "compact",
    swap: !tall,
    narrow: w < NARROW_MAX_WIDTH,
    cramped: w < CRAMPED_MAX_WIDTH,
  };
}
